/**
 * Per-employee token cache stored in localStorage.
 *
 * Tokens are keyed by employee PK so that switching between companies an
 * already-authenticated user is linked to does not require a round-trip to
 * the server unless the cached token has expired.
 *
 * To add a feature flag gate around the cache, wrap the public functions
 * with a `getFlagEnabled("your-flag-key")` check and fall through to the
 * non-cached path (return null from getCachedEmployeeToken).
 */

const KEY_PREFIX = "rmbx_emp_token_"

/**
 * Persist a Knox token for the given employee in localStorage.
 *
 * Parameters:
 * employeeId (number): The PK of the employee this token scopes to.
 * token (string): The plaintext Knox auth token.
 */
export const cacheEmployeeToken = (employeeId: number, token: string): void => {
    localStorage.setItem(`${KEY_PREFIX}${employeeId}`, token)
}

/**
 * Retrieve a previously cached token for the given employee.
 *
 * Parameters:
 * employeeId (number): The PK of the employee to look up.
 *
 * Returns:
 * string | null: The cached token, or null if none is stored.
 */
export const getCachedEmployeeToken = (employeeId: number): string | null => {
    return localStorage.getItem(`${KEY_PREFIX}${employeeId}`)
}

/**
 * Return all cached plaintext tokens currently stored in localStorage.
 * Used by the logout flow to send every cached token to the server for deletion.
 *
 * Returns:
 * string[]: Array of plaintext Knox token strings.
 */
export const getCachedEmployeeTokens = (): string[] =>
    Object.keys(localStorage)
        .filter(k => k.startsWith(KEY_PREFIX))
        .map(k => localStorage.getItem(k) as string)

/**
 * Remove all per-employee cached tokens from localStorage.
 * Call this on logout so stale tokens do not persist across user sessions.
 */
export const clearAllEmployeeTokenCache = (): void => {
    Object.keys(localStorage)
        .filter(k => k.startsWith(KEY_PREFIX))
        .forEach(k => localStorage.removeItem(k))
}
